import React, { createContext, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { getStreakData, updateStreak } from '../util';
import { useAppResources } from './AppResourcesContext';
import { useUserProfile } from './UserProfileContext';

const StreakContext = createContext(null);

const isObject = (value) => value != null && typeof value === 'object' && !Array.isArray(value);

const mapStreakResourceToState = (resource) => {
  if (!isObject(resource)) {
    return null;
  }

  return {
    currentStreak: resource.current_streak || 0,
    longestStreak: resource.longest_streak || 0,
    lastPlayedDate: resource.last_played_date || null,
    streakStartDate: resource.streak_start_date || null,
    completedDatesLast7Days: Array.isArray(resource.completed_dates_last_7_days)
      ? resource.completed_dates_last_7_days
      : null,
    completedDatesCurrentWeek: Array.isArray(resource.completed_dates_current_week)
      ? resource.completed_dates_current_week
      : null,
  };
};

const mapStreakStateToResource = (nextState, previousResource = {}) => {
  if (!isObject(nextState)) {
    return previousResource;
  }

  return {
    ...previousResource,
    current_streak: nextState.currentStreak ?? previousResource.current_streak ?? 0,
    longest_streak: nextState.longestStreak ?? previousResource.longest_streak ?? 0,
    last_played_date: nextState.lastPlayedDate !== undefined
      ? nextState.lastPlayedDate
      : previousResource.last_played_date ?? null,
    streak_start_date: nextState.streakStartDate !== undefined
      ? nextState.streakStartDate
      : previousResource.streak_start_date ?? null,
    completed_dates_last_7_days: Array.isArray(nextState.completedDatesLast7Days)
      ? nextState.completedDatesLast7Days
      : Array.isArray(previousResource.completed_dates_last_7_days)
        ? previousResource.completed_dates_last_7_days
        : [],
    completed_dates_current_week: Array.isArray(nextState.completedDatesCurrentWeek)
      ? nextState.completedDatesCurrentWeek
      : Array.isArray(previousResource.completed_dates_current_week)
        ? previousResource.completed_dates_current_week
        : [],
  };
};

export const useStreak = () => {
  const context = useContext(StreakContext);
  if (!context) {
    return {
      streakData: null,
      setStreakData: () => {},
      isLoading: false,
      updateStreakAndShowAlert: () => ({ streakData: null, isStreakExtended: false }),
      refetchStreakFromBackend: async () => null,
    };
  }
  return context;
};

export const StreakProvider = ({ children }) => {
  const { isSignedIn } = useUser();
  const { appResources, updateAppResourcesLocally } = useAppResources();
  const { isLoading: profileLoading, refreshProfile } = useUserProfile();

  const streakResource = isObject(appResources?.streak) ? appResources.streak : null;
  const streakData = useMemo(() => {
    const mappedStreak = mapStreakResourceToState(streakResource);
    if (mappedStreak) {
      return mappedStreak;
    }
    return getStreakData();
  }, [streakResource]);

  const setStreakData = useCallback((updatesOrUpdater) => {
    updateAppResourcesLocally((currentResources) => {
      const currentStreak = isObject(currentResources?.streak)
        ? currentResources.streak
        : {};
      const currentState = mapStreakResourceToState(currentStreak) || {
        currentStreak: 0,
        longestStreak: 0,
        lastPlayedDate: null,
        streakStartDate: null,
        completedDatesLast7Days: null,
        completedDatesCurrentWeek: null,
      };
      const updates = typeof updatesOrUpdater === 'function'
        ? updatesOrUpdater(currentState)
        : updatesOrUpdater;
      if (!isObject(updates)) {
        return { streak: currentStreak };
      }

      return {
        streak: mapStreakStateToResource(updates, currentStreak),
      };
    });
  }, [updateAppResourcesLocally]);

  const updateStreakAndShowAlert = useCallback(() => {
    try {
      const oldStreak = streakData?.currentStreak || 0;
      const newStreakData = updateStreak();
      setStreakData(newStreakData);

      const wasStreakExtended = newStreakData.currentStreak > oldStreak;

      if (wasStreakExtended) {
        const today = new Date().toISOString().split('T')[0];
        const updatedStreakData = { ...newStreakData, lastStreakAlertDate: today };
        setStreakData(updatedStreakData);
        localStorage.setItem('streak_data', JSON.stringify(updatedStreakData));
      }

      return {
        streakData: newStreakData,
        isStreakExtended: wasStreakExtended,
      };
    } catch (error) {
      console.error('Error updating streak:', error);
      return {
        streakData: null,
        isStreakExtended: false,
      };
    }
  }, [streakData, setStreakData]);

  const refetchStreakFromBackend = useCallback(async () => {
    try {
      const refreshedProfile = await refreshProfile();
      const mapped = mapStreakResourceToState(refreshedProfile?.app_resources?.streak);
      if (mapped) {
        return mapped;
      }
      return streakData;
    } catch (error) {
      return streakData;
    }
  }, [refreshProfile, streakData]);

  const prevIsSignedInRef = useRef(isSignedIn);
  useEffect(() => {
    const wasSignedIn = prevIsSignedInRef.current;
    if (!wasSignedIn && isSignedIn) {
      refetchStreakFromBackend().catch(() => {});
    }
    prevIsSignedInRef.current = isSignedIn;
  }, [isSignedIn, refetchStreakFromBackend]);

  const value = useMemo(() => ({
    streakData,
    setStreakData,
    isLoading: profileLoading,
    updateStreakAndShowAlert,
    refetchStreakFromBackend,
  }), [
    streakData,
    setStreakData,
    profileLoading,
    updateStreakAndShowAlert,
    refetchStreakFromBackend,
  ]);

  return (
    <StreakContext.Provider value={value}>
      {children}
    </StreakContext.Provider>
  );
};
